import { Controller } from "@hotwired/stimulus"

// "Does this come in servings or units, as well as grams?"
//
// A GATE, NOT A BURIED ESCAPE. It asks about the FOOD rather than about the
// person's willingness, so both answers are equally respectable, and the
// shape of the food is explicit before any fields appear.
//
// Yes by default: a packet nearly always prints a serving size, and the
// units this captures are what the library is short of — only 19% of foods
// have a unit a cook would recognise.
export default class extends Controller {
  static targets = [
    "panel", "none", "serving", "unitName", "unitAmount", "derived",
    "servingName", "servingStore", "ratio"
  ]

  connect() {
    this.change()
  }

  // WHICH QUESTION THIS GATE ASKS. The radios were looked up by a hardcoded
  // name, so the nested gate read the outer one's answer and both panels
  // moved together — answering "No" to the named unit hid nothing, because
  // the value it consulted belonged to the other question.
  //
  // Scoped to this controller's own element and named by a value, so a
  // third gate needs no new code.
  static values = { tolerance: { type: Number, default: 0.015 }, field: String }

  get answer() {
    const name = this.fieldValue || "food_has_units"

    return this.element.querySelector(`input[name='${name}']:checked`)?.value
  }

  change() {
    const asking = this.answer !== "no"

    if (this.hasPanelTarget) this.panelTarget.hidden = !asking
    if (this.hasNoneTarget) this.noneTarget.hidden = asking

    // DISABLED FIELDS DO NOT POST. Answering "No" must save a food with no
    // FoodUnit at all rather than one carrying a blank row, which would
    // fail validation on a step the person has just said does not apply.
    this.fields.forEach((field) => { field.disabled = !asking })

    // REQUIRED ONLY WHILE IT IS ASKED FOR. Answering Yes and leaving the
    // serving blank saved a food with no portion at all — the one thing
    // this step exists to capture. required must come OFF when the panel
    // is hidden, or a disabled-and-required field blocks the submit with
    // a message pointing at something nobody can see.
    if (this.hasServingTarget) this.servingTarget.required = asking

    // A NAMED UNIT NEEDS ITS AMOUNT. One without the other converts
    // nothing, and the model refuses it — the error arrived after the whole
    // form was filled in and appeared nowhere the person was looking. Both
    // or neither, and neither while the panel is hidden.
    if (this.hasUnitNameTarget && this.hasUnitAmountTarget) {
      this.unitNameTarget.required = asking
      this.unitAmountTarget.required = asking
    }

    if (asking) this.derive()
  }

  get fields() {
    return [
      ...(this.hasServingTarget ? [ this.servingTarget ] : []),
      ...(this.hasUnitNameTarget ? [ this.unitNameTarget ] : []),
      ...(this.hasUnitAmountTarget ? [ this.unitAmountTarget ] : [])
    ]
  }

  // 88 g and a 44 g slice means a serving is 2 slices. Derived on demand,
  // never stored: correcting the slice updates it, and there is no column
  // to go stale.
  derive() {
    this.storeServing()

    // THE SERVING LIVES ON THE OUTER GATE, the named unit on the inner one:
    // the second question is nested inside the first's panel, and Stimulus
    // scopes a target to the nearest controller of the same identity. So
    // the inner controller cannot see the serving through its own target,
    // and the ratio would never compute.
    //
    // Read from the DOM rather than reaching for the other controller
    // instance: one query, no coupling between the two, and it works
    // whichever of them the event fired on.
    // GUARDED, because a target getter THROWS when the target is absent —
    // `?.` does not help, since the error happens before the optional chain
    // is reached. The outer gate has no unitAmount (that belongs to the
    // nested one), so derive() raised during connect and everything after
    // it in that connect never ran, which is what broke the stepper.
    if (!this.hasDerivedTarget || !this.hasUnitAmountTarget || !this.hasUnitNameTarget) {
      return
    }

    const serving = parseFloat(this.servingField?.value)
    const unit = parseFloat(this.unitAmountTarget.value)
    const name = this.unitNameTarget.value.trim()

    const ratio = this.wholeRatio(serving, unit)
    if (!ratio || !name) {
      this.derivedTarget.hidden = true
      return
    }

    // ONLY THE FIGURE. The sentence and its question mark are markup —
    // textContent would have flattened both, and the icon with them.
    const plural = ratio === 1 ? name : `${name}s`
    if (this.hasRatioTarget) this.ratioTarget.textContent = `${ratio} ${plural}`
    this.derivedTarget.hidden = false
  }

  // The serving is stored as a second FoodUnit rather than a column: one
  // spine, every unit saying how many base units one of it is.
  // The serving field, wherever it sits. See derive().
  get servingField() {
    return this.hasServingTarget
      ? this.servingTarget
      : document.querySelector("[data-unit-gate-target='serving']")
  }

  storeServing() {
    if (!this.hasServingStoreTarget) return

    const serving = parseFloat(this.servingField?.value)
    const usable = !isNaN(serving) && serving > 0

    this.servingStoreTarget.value = usable ? serving : ""
    this.servingStoreTarget.disabled = !usable
    if (this.hasServingNameTarget) this.servingNameTarget.disabled = !usable
  }

  // Mirrors FoodUnit.whole_ratio. "1 serving (2 slices)" is useful; "1
  // serving (8.57 bars)" is noise — and it is the shape a mistyped weight
  // takes, so staying silent is the error check.
  wholeRatio(larger, smaller) {
    if (!(larger > 0) || !(smaller > 0)) return null

    const ratio = larger / smaller
    if (ratio < 1) return null

    const nearest = Math.round(ratio)
    if (nearest === 0) return null
    if (Math.abs((ratio - nearest) / nearest) > this.toleranceValue) return null

    return nearest
  }
}
